/**
 * CR054 + CR060: Pack Builder Component
 * Drag-and-drop interface for building prompt packs
 * CR060: Uses phase registry for consistent phase definitions
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { 
  Plus, 
  Minus, 
  GripVertical, 
  ChevronDown, 
  ChevronUp, 
  Search, 
  AlertTriangle,
  Info,
  Loader2,
  Save
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { 
  PHASE_REGISTRY, 
  getAllPhases, 
  getPhaseColor,
  type PhaseType 
} from "@/lib/poc-pipeline/phaseRegistry";

interface Prompt {
  id: string;
  key: string;
  name: string;
  phase: string;
  prompt_text: string;
  default_model_hint: string;
  temperature: number | null;
  is_active: boolean;
  archived_at: string | null;
  version: number;
  output_schema_json: any | null;
  input_schema_json: any | null;
}

interface PackItem {
  id: string;
  prompt_id: string;
  order_index: number;
  enabled: boolean;
  model_override: string | null;
  temperature_override: number | null;
  stage: string | null;
  prompt: Prompt;
}

interface PackBuilderProps {
  packId: string;
  packName: string;
  packKind?: 'SCAN' | 'WORKFLOW'; // CR466
  onClose: () => void;
  onSave: () => void;
}

// CR466: Only enforce required stages for SCAN packs
const SCAN_REQUIRED_STAGES = ["SYSTEM", "SCAN", "EXTRACT", "VALIDATE"];

// CR060: Use phase registry for consistent phase definitions
const STAGE_ORDER: PhaseType[] = getAllPhases().sort((a, b) => 
  PHASE_REGISTRY[a].order - PHASE_REGISTRY[b].order
);

// CR060: Get colors from phase registry
const PHASE_COLORS: Record<string, string> = Object.fromEntries(
  getAllPhases().map(phase => [phase, PHASE_REGISTRY[phase].color])
);

function SortableItem({ item, onToggle, onModelChange, onTempChange, onRemove }: {
  item: PackItem;
  onToggle: (id: string, enabled: boolean) => void;
  onModelChange: (id: string, model: string | null) => void;
  onTempChange: (id: string, temp: number | null) => void;
  onRemove: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [showDetails, setShowDetails] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 bg-card ${!item.enabled ? "opacity-50" : ""}`}
    >
      <div className="flex items-center gap-2">
        <button {...attributes} {...listeners} className="cursor-grab touch-none flex-shrink-0">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        
        <Switch
          checked={item.enabled}
          onCheckedChange={(checked) => onToggle(item.id, checked)}
          className="flex-shrink-0"
        />
        
        <Badge className={`${PHASE_COLORS[item.prompt.phase] || "bg-gray-500"} flex-shrink-0`}>
          {item.prompt.phase}
        </Badge>
        
        <span className="font-medium flex-1 truncate min-w-0">{item.prompt.name}</span>
        
        <span className="text-xs text-muted-foreground flex-shrink-0">v{item.prompt.version}</span>
        
        <Button variant="ghost" size="icon" onClick={() => setShowDetails(!showDetails)} className="flex-shrink-0 h-8 w-8">
          {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>
      
      {showDetails && (
        <div className="mt-3 pt-3 border-t space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Model Override</Label>
              <Select
                value={item.model_override || "__default__"}
                onValueChange={(v) => onModelChange(item.id, v === "__default__" ? null : v)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Use default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Use default ({item.prompt.default_model_hint.replace("gemini-2.5-", "")})</SelectItem>
                  <SelectItem value="gemini-2.5-flash-lite">Flash-Lite</SelectItem>
                  <SelectItem value="gemini-2.5-flash">Flash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Temperature Override</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="2"
                className="h-8"
                value={item.temperature_override ?? ""}
                placeholder={String(item.prompt.temperature ?? "0.3")}
                onChange={(e) => onTempChange(item.id, e.target.value ? parseFloat(e.target.value) : null)}
              />
            </div>
          </div>
          
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              View Prompt Text
            </summary>
            <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-x-auto whitespace-pre-wrap max-h-[200px]">
              {item.prompt.prompt_text}
            </pre>
          </details>
          
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => onRemove(item.id)}
            className="w-full"
          >
            <Minus className="h-4 w-4 mr-2" />
            Remove from Pack
          </Button>
        </div>
      )}
    </div>
  );
}

export function PackBuilder({ packId, packName, packKind = 'SCAN', onClose, onSave }: PackBuilderProps) {
  const [availablePrompts, setAvailablePrompts] = useState<Prompt[]>([]);
  const [packItems, setPackItems] = useState<PackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [phaseFilter, setPhaseFilter] = useState<string>("all");
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchData = useCallback(async () => {
    try {
      const [promptsResult, itemsResult] = await Promise.all([
        supabase
          .from("prompts")
          .select("*")
          .is("deleted_at", null)
          .order("phase")
          .order("name"),
        supabase
          .from("prompt_pack_items")
          .select("*, prompts(*)")
          .eq("prompt_pack_id", packId)
          .order("order_index")
      ]);

      if (promptsResult.error) throw promptsResult.error;
      if (itemsResult.error) throw itemsResult.error;

      setAvailablePrompts(promptsResult.data || []);
      
      const items = (itemsResult.data || []).map((item: any) => ({
        id: item.id,
        prompt_id: item.prompt_id,
        order_index: item.order_index,
        enabled: item.enabled,
        model_override: item.model_override,
        temperature_override: item.temperature_override,
        stage: item.stage,
        prompt: item.prompts
      }));
      
      setPackItems(items);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load pack data");
    } finally {
      setLoading(false);
    }
  }, [packId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPackItems((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order_index
        return newItems.map((item, idx) => ({ ...item, order_index: idx }));
      });
      setHasChanges(true);
    }
  };

  const handleAddPrompt = (prompt: Prompt) => {
    // Check if already in pack
    if (packItems.some(item => item.prompt_id === prompt.id)) {
      toast.error("Prompt already in pack");
      return;
    }

    const newItem: PackItem = {
      id: `new-${Date.now()}`, // Temporary ID
      prompt_id: prompt.id,
      order_index: packItems.length,
      enabled: true,
      model_override: null,
      temperature_override: null,
      stage: prompt.phase,
      prompt: prompt
    };

    setPackItems([...packItems, newItem]);
    setHasChanges(true);
  };

  const handleRemoveItem = (itemId: string) => {
    setPackItems(packItems.filter(item => item.id !== itemId));
    setHasChanges(true);
  };

  const handleToggleItem = (itemId: string, enabled: boolean) => {
    setPackItems(packItems.map(item => 
      item.id === itemId ? { ...item, enabled } : item
    ));
    setHasChanges(true);
  };

  const handleModelChange = (itemId: string, model: string | null) => {
    setPackItems(packItems.map(item => 
      item.id === itemId ? { ...item, model_override: model } : item
    ));
    setHasChanges(true);
  };

  const handleTempChange = (itemId: string, temp: number | null) => {
    setPackItems(packItems.map(item => 
      item.id === itemId ? { ...item, temperature_override: temp } : item
    ));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Delete all existing items
      await supabase
        .from("prompt_pack_items")
        .delete()
        .eq("prompt_pack_id", packId);

      // Insert new items
      if (packItems.length > 0) {
        const itemsToInsert = packItems.map((item, idx) => ({
          prompt_pack_id: packId,
          prompt_id: item.prompt_id,
          order_index: idx,
          enabled: item.enabled,
          model_override: item.model_override,
          temperature_override: item.temperature_override,
          stage: item.prompt.phase
        }));

        const { error } = await supabase
          .from("prompt_pack_items")
          .insert(itemsToInsert);

        if (error) throw error;
      }

      toast.success("Pack saved");
      setHasChanges(false);
      onSave();
    } catch (err) {
      console.error("Error saving pack:", err);
      toast.error("Failed to save pack");
    } finally {
      setSaving(false);
    }
  };

  // Filter available prompts
  const filteredAvailable = availablePrompts.filter(p => {
    const matchesSearch = 
      p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhase = phaseFilter === "all" || p.phase === phaseFilter;
    const notArchived = !p.archived_at;
    return matchesSearch && matchesPhase && notArchived;
  });

  // CR466: Only check required stages for SCAN packs
  const enabledItems = packItems.filter(i => i.enabled);
  const enabledPhases = new Set(enabledItems.map(i => i.prompt.phase));
  const missingStages = packKind === 'SCAN'
    ? SCAN_REQUIRED_STAGES.filter(s => !enabledPhases.has(s))
    : [];

  // CR467: Workflow validation
  const workflowErrors: string[] = [];
  const workflowWarnings: string[] = [];
  if (packKind === 'WORKFLOW') {
    if (enabledItems.length === 0) {
      workflowWarnings.push("Workflow pack has no enabled prompts — it won't run until prompts are added.");
    }
    // S5.1 specific checks — warning only, not blocking
    if (enabledItems.length > 0 && !enabledPhases.has('LOCATE_SNIPPET')) {
      workflowWarnings.push("This pack won't run until it has a LOCATE_SNIPPET prompt enabled.");
    }
    // Check for missing output_schema_json — only on prompts that need structured output (not SYSTEM)
    const missingSchemaKeys = enabledItems
      .filter(i => {
        const p = i.prompt;
        if (!p) return false;
        if (p.phase === "SYSTEM") return false;
        // For S5.1 workflow packs, only LOCATE_SNIPPET needs strict schema
        if (p.phase === "LOCATE_SNIPPET") return !p.output_schema_json;
        return false;
      })
      .map(i => i.prompt?.key)
      .filter(Boolean);
    if (missingSchemaKeys.length > 0) {
      workflowWarnings.push(`Missing output_schema_json for: ${missingSchemaKeys.join(", ")} — parsing may fail.`);
    }
  }

  // Get execution order from actual pack items (in order_index order)
  const executionOrder = packItems
    .filter(i => i.enabled)
    .sort((a, b) => a.order_index - b.order_index)
    .map(i => ({ phase: i.prompt.phase, name: i.prompt.name }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Pack Builder: {packName}</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            Save Pack
          </Button>
        </div>
      </div>

      {/* Warnings - SCAN missing required stages */}
      {missingStages.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-yellow-800 dark:text-yellow-200">
                Missing required stages: <strong>{missingStages.join(", ")}</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CR467: Workflow Pack Health panel */}
      {packKind === 'WORKFLOW' && (workflowErrors.length > 0 || workflowWarnings.length > 0) && (
        <div className="space-y-2">
          {workflowErrors.map((err, i) => (
            <div key={`we-${i}`} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-200">{err}</p>
              </div>
            </div>
          ))}
          {workflowWarnings.map((warn, i) => (
            <div key={`ww-${i}`} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 dark:text-yellow-200">{warn}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Execution Order Preview - shows actual order from pack config */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">Execution Order ({executionOrder.length} steps)</CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-1">
            {executionOrder.length === 0 ? (
              <span className="text-sm text-muted-foreground">No enabled prompts</span>
            ) : (
              executionOrder.map((item, idx) => (
                <div key={idx} className="flex items-center">
                  <Badge className={PHASE_COLORS[item.phase] || "bg-gray-500"}>
                    {item.phase}
                  </Badge>
                  {idx < executionOrder.length - 1 && (
                    <span className="mx-1 text-muted-foreground">→</span>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 h-[500px]">
        {/* Left Panel: Available Prompts */}
        <Card className="flex flex-col">
          <CardHeader className="py-3 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Available Prompts</CardTitle>
              <span className="text-xs text-muted-foreground">{filteredAvailable.length} prompts</span>
            </div>
            <div className="flex gap-2 mt-2">
              <Select value={phaseFilter} onValueChange={setPhaseFilter}>
                <SelectTrigger className="w-[120px] h-8">
                  <SelectValue placeholder="All phases" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All phases</SelectItem>
                  {STAGE_ORDER.map((phase) => (
                    <SelectItem key={phase} value={phase}>{phase}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8"
                />
              </div>
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-2">
              {filteredAvailable.map((prompt) => {
                const isInPack = packItems.some(item => item.prompt_id === prompt.id);
                return (
                  <div 
                    key={prompt.id} 
                    className={`border rounded-lg p-3 ${isInPack ? "opacity-50 bg-muted" : "hover:bg-accent cursor-pointer"}`}
                    onClick={() => !isInPack && handleAddPrompt(prompt)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge className={PHASE_COLORS[prompt.phase] || "bg-gray-500"}>
                          {prompt.phase}
                        </Badge>
                        <span className="font-medium truncate">{prompt.name}</span>
                      </div>
                      {!isInPack && (
                        <Button variant="ghost" size="icon" className="flex-shrink-0">
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{prompt.key}</p>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </Card>

        {/* Right Panel: Pack Items */}
        <Card className="flex flex-col">
          <CardHeader className="py-3 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Prompts in Pack</CardTitle>
              <span className="text-xs text-muted-foreground">
                {packItems.filter(i => i.enabled).length} enabled / {packItems.length} total
              </span>
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={packItems.map(i => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {packItems.map((item) => (
                    <SortableItem
                      key={item.id}
                      item={item}
                      onToggle={handleToggleItem}
                      onModelChange={handleModelChange}
                      onTempChange={handleTempChange}
                      onRemove={handleRemoveItem}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              
              {packItems.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No prompts in pack</p>
                  <p className="text-sm">Click prompts on the left to add them</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </Card>
      </div>

      {/* Diagnostics Panel */}
      <Collapsible open={showDiagnostics} onOpenChange={setShowDiagnostics}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between">
            <span className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              Diagnostics
            </span>
            {showDiagnostics ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="p-4 bg-muted rounded-lg text-sm space-y-2 mt-2">
            <p><strong>Active Filters:</strong> Phase={phaseFilter}, Search="{searchQuery}"</p>
            <p><strong>Total Available:</strong> {availablePrompts.length} prompts</p>
            <p><strong>Filtered Results:</strong> {filteredAvailable.length} prompts</p>
            <p><strong>Pack Items:</strong> {packItems.length} ({packItems.filter(i => i.enabled).length} enabled)</p>
            <p><strong>Last Fetch:</strong> {new Date().toISOString()}</p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
